import React, {
	useEffect,
	useRef,
	useState,
} from 'react';

import Button from '../../../../component/ui/Button';
import MasterProductModal from '../../../../component/modal/MasterProductModal.jsx';
import InputField from '../../../../component/form/FormInput';
import { PageSmallLoader } from '../../../../component/PageLoader';

import { toast } from 'react-toastify';

import {
	getDefaultProductList,
	masterProductClone,
} from '../../../../services/products';

import { useNavigate } from 'react-router-dom';

export default function ProductMasterModel({
	isProductMasterOpen,
	setIsProductMasterOpen,
}) {
	const [searchText, setSearchText] = useState('');
	const [isLoader, setLoader] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isProductClone, setIsProductClone] = useState(false);
	const [productList, setProductList] = useState([]);
	const [total, setTotal] = useState(0);

	const [params, setParams] = useState({
		page: 1,
		pageSize: 30,
		search: '',
	});

	const [selectedProductId, setSelectedProductId] =
		useState(null);

	const isFirstRender = useRef(true);

	const navigate = useNavigate();

	const suburl = localStorage.getItem('suburl');

	/*
	 * =========================================================
	 * CLOSE
	 * =========================================================
	 */

	const handleClose = () => {
		setIsProductMasterOpen(false);
	};

	/*
	 * =========================================================
	 * CLONE PRODUCT
	 * =========================================================
	 */

	const handleMasterProductClone = async () => {
		if (!selectedProductId) {
			toast.error(
				'Please select one checkbox of product list',
			);
			return;
		}

		try {
			setIsProductClone(true);

			const res = await masterProductClone(
				selectedProductId,
			);

			if (!res?.success) {
				toast.error(
					res?.message || 'Something went wrong!',
				);
				return;
			}

			const { id } = res.data;

			toast.success(res.message);

			navigate(
				`/user/${suburl}/product-create/id=${id}&status=draft`,
				{
					state: {
						isEdit: true,
					},
				},
			);

			setIsProductMasterOpen(false);
			setSelectedProductId(null);
		} catch (error) {
			toast.error(
				error?.message || 'Something went wrong!',
			);
		} finally {
			setIsProductClone(false);
		}
	};

	/*
	 * =========================================================
	 * PRODUCT SELECTION
	 * =========================================================
	 */

	const toggleProduct = (id) => {
		setSelectedProductId((previousId) =>
			previousId === id ? null : id,
		);
	};

	/*
	 * =========================================================
	 * SEARCH DEBOUNCE
	 * =========================================================
	 */

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		const debounceTimer = setTimeout(() => {
			setParams((previous) => ({
				...previous,
				page: 1,
				search: searchText.trim(),
			}));

			setSelectedProductId(null);
		}, 500);

		return () => {
			clearTimeout(debounceTimer);
		};
	}, [searchText]);

	/*
	 * =========================================================
	 * FETCH PRODUCTS
	 * =========================================================
	 */

	useEffect(() => {
		// page 1 (a fresh search, a reset, or the initial open) replaces the
		// list and shows the full loader; page > 1 (Load More) appends to
		// what's already on screen and only shows the small inline loader,
		// so existing results don't disappear while the next page loads.
		const isLoadMore = params.page > 1;

		const fetchDefaultProductList = async () => {
			try {
				if (isLoadMore) {
					setIsLoadingMore(true);
				} else {
					setLoader(true);
				}

				const { master_products, total: totalCount } =
					await getDefaultProductList(params);

				setProductList((previous) =>
					isLoadMore
						? [...previous, ...(master_products || [])]
						: master_products || [],
				);
				setTotal(totalCount || 0);
			} catch (error) {
				toast.error(
					error?.message || 'Something went wrong!',
				);
			} finally {
				setLoader(false);
				setIsLoadingMore(false);
			}
		};

		fetchDefaultProductList();
	}, [params]);

	/*
	 * =========================================================
	 * LOAD MORE
	 * =========================================================
	 */

	const hasMore = productList.length < total;

	const handleLoadMore = () => {
		if (isLoader || isLoadingMore || !hasMore) return;

		setParams((previous) => ({
			...previous,
			page: previous.page + 1,
		}));
	};

	/*
	 * =========================================================
	 * RESET
	 * =========================================================
	 */

	const handleReset = () => {
		setSearchText('');
		setSelectedProductId(null);

		setParams((previous) => ({
			...previous,
			page: 1,
			search: '',
		}));
	};

	/*
	 * =========================================================
	 * FOOTER
	 * =========================================================
	 */

	const modalFooter = (
		<>
			<Button
				type="button"
				button="Cancel"
				className="mt-0 px-4 btn-gray"
				onClick={handleClose}
			/>

			<Button
				type="button"
				button="Clone"
				loader={isProductClone}
				disabled={isProductClone}
				className="mt-0 px-4 btn-primary"
				onClick={handleMasterProductClone}
			/>
		</>
	);

	return (
		<MasterProductModal
			isOpen={isProductMasterOpen}
			onClose={handleClose}
			title="Master Products"
			footer={modalFooter}
			size="md"
		>
			{/* =====================================================
          SEARCH
      ====================================================== */}

			<div className="master-product-search">
				<div className="master-product-search-input">
					<InputField
						label=""
						placeholder="Search"
						type="text"
						autoComplete="off"
						required={false}
						autoFocus={false}
						value={searchText}
						onChange={setSearchText}
					/>
				</div>

				<button
					type="button"
					className="master-product-reset"
					onClick={handleReset}
				>
					Reset
				</button>
			</div>

			{/* =====================================================
          DESCRIPTION
      ====================================================== */}

			<div className="master-product-description">
				Select the product you want to clone.
			</div>

			{/* =====================================================
          ONLY SCROLLABLE AREA
      ====================================================== */}

			<div className="master-product-list">
				{isLoader ? (
					<div className="master-product-loader">
						<PageSmallLoader />
					</div>
				) : productList.length === 0 ? (
					<div className="master-product-empty">
						No Product found.
					</div>
				) : (
					productList.map((product) => {
						const isSelected =
							product.id === selectedProductId;

						return (
							<label
								key={product.id}
								className={`master-product-item ${isSelected ? 'is-selected' : ''
									}`}
							>
								<input
									type="checkbox"
									checked={isSelected}
									onChange={() =>
										toggleProduct(product.id)
									}
								/>

								<span className="master-product-name">
									{product.product_name}
								</span>
							</label>
						);
					})
				)}

				{!isLoader && hasMore && (
					<div className="master-product-load-more">
						{isLoadingMore ? (
							<PageSmallLoader />
						) : (
							<button
								type="button"
								className="master-product-load-more-btn"
								onClick={handleLoadMore}
							>
								Load More
							</button>
						)}
					</div>
				)}
			</div>
		</MasterProductModal>
	);
}